import React from 'react';
import type { CSSProperties, ReactNode, ButtonHTMLAttributes } from 'react';
import useStyles from '../../theme/useStyles';
import { useReadOnly } from '../../utils/readOnly';
import { iconify } from './Icon';

// variant: primary | secondary | danger | ghost | tab
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'tab';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Only meaningful for variant="tab". */
  active?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
  style?: CSSProperties;
  isDarkMode?: boolean;
  /**
   * Marks a control on a write path that is neither a danger nor a submit
   * button — a plain "Save" that calls its handler directly, or an "Edit" that
   * opens the form that saves. Hidden for read-only users, the same as danger
   * and submit buttons, so a read-only module shows View but not Edit (CHM-071).
   */
  write?: boolean;
}

const Button = ({
  variant = 'primary',
  size = 'md',
  active = false,
  disabled = false,
  fullWidth = false,
  icon,
  style,
  children,
  isDarkMode,
  write = false,
  type,
  ...rest
}: ButtonProps) => {
  const s = useStyles(isDarkMode);
  const readOnly = useReadOnly();

  // Every write path is hidden for a read-only user: destructive buttons
  // (variant="danger"), form submits (type="submit"), and any control flagged
  // `write` that saves directly without being either. Navigation and view
  // controls (tabs, Back, View, Print) stay (CHM-071).
  if (readOnly && (variant === 'danger' || type === 'submit' || write)) return null;

  const base =
    variant === 'tab' ? (active ? s.activeTabBtn : s.inactiveTabBtn)
    : variant === 'secondary' ? s.viewBtn
    : variant === 'ghost' ? s.actionBtn
    : variant === 'danger' ? s.dangerBtn
    : s.submitBtn;

  // `sm` is used for the Edit / Delete / Print controls packed into table rows.
  // 32px keeps the row rhythm tight while staying a comfortable target; the
  // 44px touch guideline would make every data table a third taller.
  const sizing =
    size === 'sm' ? { padding: '5px 10px', minHeight: '32px', fontSize: s.t.fontSize.base }
    : size === 'lg' ? { padding: '12px 24px', minHeight: '44px', fontSize: s.t.fontSize.lg }
    : { minHeight: '36px' };

  return (
    <button
      disabled={disabled}
      type={type}
      style={{
        ...base,
        ...sizing,
        ...(fullWidth ? { width: '100%' } : {}),
        ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        ...style
      }}
      {...rest}
    >
      {icon && <span className="notranslate" translate="no">{iconify(icon)}</span>}
      {iconify(children, size === 'sm' ? 14 : 15)}
    </button>
  );
};

export default Button;
